import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SijilatyLogo } from "@/components/ui/SijilatyLogo";
import { useAuthUser } from "@/lib/auth";
import { fetchNavCounters, EMPTY_NAV_COUNTERS, fetchMe } from "@/lib/queries";
import type { NavCounters } from "@/lib/queries";
import { useAsync } from "@/lib/useAsync";
import { cn } from "@/lib/cn";
import { nameInitial } from "@/lib/names";
import { usePortal, useIsAdminPortal } from "@/lib/portal";
import { useSidebarCollapsed } from "@/lib/prefs";
import {
  IconHome,
  IconStethoscope,
  IconClipboard,
  IconUsers,
  IconDoc,
  IconShield,
  IconCard,
  IconEdit,
  IconAudit,
  IconSettings,
  IconBell,
  IconPage,
  IconChart,
  IconDownload,
  IconKey,
} from "@/components/icons";

interface NavItem {
  to: string;
  labelKey: string;
  icon: React.ReactNode;
  badge?: number | string;
  children?: { to: string; labelKey: string }[];
}

interface NavSection {
  labelKey?: NavGroupKey;
  items: NavItem[];
}

/**
 * Single shared vocabulary of sidebar section groups — BOTH the central and
 * admin schemas draw their group labels from this set, so the two can't drift
 * into divergent grouping conventions. Each value is an i18n key under `nav.*`.
 * [audit P0-3a]
 */
const NAV_GROUPS = {
  management: "nav.management",
  operations: "nav.operations",
  account: "nav.account",
  insights: "nav.insights",
  catalog: "nav.catalog",
  engagement: "nav.engagement",
  content: "nav.content",
  configuration: "nav.configuration",
  developer: "nav.developer",
  system: "nav.system",
} as const;
type NavGroupKey = (typeof NAV_GROUPS)[keyof typeof NAV_GROUPS];

const baseSections: NavSection[] = [
  {
    items: [{ to: "/", labelKey: "nav.dashboard", icon: <IconHome /> }],
  },
  {
    labelKey: NAV_GROUPS.management,
    items: [
      {
        to: "/doctors/active",
        labelKey: "nav.doctors",
        icon: <IconStethoscope />,
        children: [
          { to: "/doctors/active", labelKey: "nav.doctorsActive" },
          { to: "/doctors/inactive", labelKey: "nav.doctorsInactive" },
        ],
      },
      { to: "/sessions", labelKey: "nav.sessions", icon: <IconClipboard /> },
      { to: "/reviewers", labelKey: "nav.reviewers", icon: <IconUsers /> },
      // { to: "/contracts", labelKey: "nav.contracts", icon: <IconDoc /> },
      // { to: "/chronic-disease-consent", labelKey: "nav.chronicDiseaseConsent", icon: <IconClipboard /> },
    ],
  },
  {
    labelKey: NAV_GROUPS.account,
    items: [
      // { to: "/subscription", labelKey: "nav.subscription", icon: <IconCard /> },
      { to: "/admins", labelKey: "nav.admins", icon: <IconUsers /> },
      { to: "/roles", labelKey: "nav.roles", icon: <IconShield /> },
      { to: "/activities", labelKey: "nav.activities", icon: <IconAudit /> },
      { to: "/settings", labelKey: "nav.settings", icon: <IconSettings /> },
    ],
  },
  {
    labelKey: NAV_GROUPS.developer,
    items: [
      { to: "/api-keys", labelKey: "nav.apiKeys", icon: <IconKey /> },
      { to: "/api-docs", labelKey: "nav.apiDocs", icon: <IconDoc /> },
    ],
  },
];

const adminSectionsBase: NavSection[] = [
  {
    items: [{ to: "/admin", labelKey: "nav.dashboard", icon: <IconHome /> }],
  },
  {
    labelKey: NAV_GROUPS.management,
    items: [
      { to: "/admin/centrals", labelKey: "nav.centrals", icon: <IconShield /> },
      { to: "/admin/doctors", labelKey: "nav.doctors", icon: <IconStethoscope /> },
      { to: "/admin/reviewers", labelKey: "nav.reviewers", icon: <IconUsers /> },
      { to: "/admin/sessions", labelKey: "nav.sessions", icon: <IconClipboard /> },
    ],
  },
  {
    labelKey: NAV_GROUPS.operations,
    items: [
      { to: "/admin/subscription-requests", labelKey: "nav.subscriptionRequests", icon: <IconCard /> },
      { to: "/admin/pending-field-changes", labelKey: "nav.pendingFieldChanges", icon: <IconEdit /> },
      { to: "/admin/doctors/completeness", labelKey: "nav.doctorCompleteness", icon: <IconClipboard /> },
      { to: "/admin/moderators", labelKey: "nav.moderators", icon: <IconShield /> },
      { to: "/admin/roles", labelKey: "nav.roles", icon: <IconShield /> },
    ],
  },
  {
    labelKey: NAV_GROUPS.insights,
    items: [
      { to: "/admin/reports", labelKey: "nav.reports", icon: <IconChart /> },
      { to: "/admin/ranking", labelKey: "nav.ranking", icon: <IconChart /> },
      { to: "/admin/exports", labelKey: "nav.exports", icon: <IconDownload /> },
    ],
  },
  {
    labelKey: NAV_GROUPS.catalog,
    items: [
      { to: "/admin/packages", labelKey: "nav.packages", icon: <IconDoc /> },
      { to: "/admin/zone-pricing", labelKey: "nav.zonePricing", icon: <IconCard /> },
      { to: "/admin/zone-pricing/preset", labelKey: "zonePricingPreset.title", icon: <IconCard /> },
      { to: "/admin/promo-codes", labelKey: "nav.promoCodes", icon: <IconCard /> },
      { to: "/admin/subscriptions", labelKey: "nav.subscriptions", icon: <IconCard /> },
      { to: "/admin/specializations", labelKey: "nav.specForms", icon: <IconClipboard /> },
    ],
  },
  {
    labelKey: NAV_GROUPS.engagement,
    items: [
      { to: "/admin/witnesses", labelKey: "nav.witnesses", icon: <IconUsers /> },
      { to: "/admin/notifications", labelKey: "nav.notifications", icon: <IconBell /> },
      { to: "/admin/whatsapp-messages", labelKey: "nav.whatsappMessages", icon: <IconDoc /> },
      { to: "/admin/sms-messages", labelKey: "nav.smsMessages", icon: <IconDoc /> },
      { to: "/admin/contacts", labelKey: "nav.contacts", icon: <IconDoc /> },
    ],
  },
  {
    labelKey: NAV_GROUPS.content,
    items: [
      { to: "/admin/faqs", labelKey: "nav.faqs", icon: <IconDoc /> },
      { to: "/admin/pages", labelKey: "nav.pages", icon: <IconPage /> },
    ],
  },
  {
    labelKey: NAV_GROUPS.configuration,
    items: [
      { to: "/admin/countries", labelKey: "nav.countries", icon: <IconShield /> },
      { to: "/admin/settings", labelKey: "nav.settings", icon: <IconSettings /> },
    ],
  },
  {
    labelKey: NAV_GROUPS.system,
    items: [
      { to: "/admin/audit", labelKey: "nav.audit", icon: <IconAudit /> },
      // { to: "/admin/contract-regeneration", labelKey: "nav.contractRegeneration", icon: <IconDoc /> },
    ],
  },
];

function badgeCount(n: number): string | undefined {
  if (n <= 0) return undefined;
  return n > 99 ? "99+" : String(n);
}

/**
 * Drive the admin nav badges off the consolidated counters object (§B13).
 * Only items that have a matching counter get a badge; everything else is
 * passed through untouched so we never render a pill with no backing number.
 */
function adminSectionsWithBadges(counters: NavCounters): NavSection[] {
  return adminSectionsBase.map((section) => ({
    ...section,
    items: section.items.map((item) => {
      if (item.to === "/admin/subscription-requests") {
        return { ...item, badge: badgeCount(counters.subscriptionRequestsPending) };
      }
      if (item.to === "/admin/pending-field-changes") {
        return { ...item, badge: badgeCount(counters.pendingFieldChanges) };
      }
      return item;
    }),
  }));
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  collapsed?: boolean;
}

function CollapsibleNavItem({
  item,
  collapsed,
  adminPortal,
  onClose,
}: {
  item: NavItem;
  collapsed: boolean;
  adminPortal: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isChildActive = item.children?.some((c) => pathname.startsWith(c.to)) ?? false;
  const [open, setOpen] = useState(isChildActive);

  useEffect(() => {
    if (isChildActive) setOpen(true);
  }, [isChildActive]);

  return (
    <div className="mb-0.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={collapsed ? t(item.labelKey) : undefined}
        className={cn(
          "relative mb-0.5 flex w-full items-center gap-3 rounded-lg px-3 py-3 text-[13px] font-medium transition-all motion-reduce:transition-none",
          collapsed && "lg:justify-center lg:px-0",
          isChildActive
            ? adminPortal
              ? "border border-primary/25 bg-gradient-to-r from-primary/[0.18] to-accent/[0.08] text-white rtl:bg-gradient-to-l"
              : "border border-primary/26 bg-primary/20 text-white shadow-[0_0_20px_rgb(var(--color-primary)/0.25),inset_0_0_28px_rgb(var(--color-primary)/0.12)]"
            : "text-white/65 hover:bg-white/[0.08] hover:text-white/90",
        )}
      >
        {isChildActive && (
          <span
            className={cn(
              "absolute start-0 bottom-2 top-2 w-[3px] rounded-e",
              collapsed && "lg:hidden",
              adminPortal
                ? "bg-primary-2"
                : "brand-grad shadow-[0_0_8px_rgb(var(--color-primary)/0.6)]",
            )}
          />
        )}
        <span
          className={cn(
            "relative opacity-65",
            isChildActive && "text-primary-2 opacity-100",
          )}
        >
          {item.icon}
        </span>
        <span className={cn("flex-1 text-start", collapsed && "lg:hidden")}>{t(item.labelKey)}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn(
            "shrink-0 transition-transform motion-reduce:transition-none",
            collapsed && "lg:hidden",
            open && "rotate-90",
          )}
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      </button>
      {open && !collapsed && (
        <div className="ms-4">
          {item.children?.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              end
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  "relative mb-0.5 flex items-center gap-3 rounded-lg px-3 py-2 text-[12.5px] font-medium transition-all motion-reduce:transition-none",
                  isActive
                    ? adminPortal
                      ? "text-white"
                      : "text-white font-semibold"
                    : "text-white/50 hover:bg-white/[0.06] hover:text-white/75",
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      className={cn(
                        "absolute start-0 bottom-1.5 top-1.5 w-[2px] rounded-e",
                        adminPortal ? "bg-primary-2" : "bg-primary",
                      )}
                    />
                  )}
                  <span className="h-1 w-1 shrink-0 rounded-full bg-current opacity-40" />
                  <span>{t(child.labelKey)}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ open, onClose, collapsed: collapsedProp }: SidebarProps) {
  const { t } = useTranslation();
  // Portal resolved from shared context (route), not a drilled prop. [audit P0-3b]
  const adminPortal = useIsAdminPortal();
  // Persisted desktop collapse state. The prop (when supplied) wins so the
  // synced/storybook preview can pin a state without touching localStorage;
  // otherwise we read the user's pref, defaulting to expanded.
  const [persistedCollapsed, setPersistedCollapsed] = useSidebarCollapsed();
  const collapsed = collapsedProp ?? persistedCollapsed;
  const portal = usePortal();
  const user = useAuthUser();
  // All sidebar badge counts now come from ONE consolidated, caller-scoped
  // endpoint (§B13) instead of a fetch per list. It's portal-scoped — the
  // central counters require the `central` token ability and the admin ones
  // require `admin`, so we only ever hit the active portal's endpoint (deps
  // include `portal` so the fetch fires/refires once it's resolved). The
  // fetcher degrades gracefully (404/network → legacy per-list fetchers), so an
  // unauthenticated or pre-§B13 backend just yields zeroed counters rather than
  // console errors.
  const { data: counters } = useAsync(() => fetchNavCounters(portal), [portal], {
    cacheKey: `badge:navCounters:${portal}`,
    cacheTTL: 60_000,
  });
  const navCounters = counters ?? EMPTY_NAV_COUNTERS;

  // Central-portal brand header (logo + centre name + current admin's name),
  // spec §C. fetchMe degrades gracefully to a context built from the stored
  // user, so this never blocks rendering and the synced storybook preview (which
  // seeds `sij_user` with `central_name`) shows the brand block too. We only
  // fetch in the central portal — the admin portal keeps its existing header.
  // Only the central portal needs the /me context (brand header). Calling
  // fetchMe("central") in the admin portal hits /central/me with an admin-ability
  // token, which the portal.central middleware rejects with 403. Skip the network
  // entirely for admin and resolve to undefined — the admin header uses the stored
  // user (adminName) below and never reads me.central.
  const { data: me } = useAsync(
    () => (adminPortal ? Promise.resolve(undefined) : fetchMe("central")),
    [adminPortal],
    { cacheKey: adminPortal ? "ctx:me:admin-skip" : "ctx:me:central", cacheTTL: 300_000 },
  );
  const centralName = me?.central?.name ?? user?.central_name ?? "";
  const adminName = me?.user.name ?? user?.name ?? "";
  const centralLogoUrl = me?.central?.logoUrl;

  const sections = adminPortal
    ? adminSectionsWithBadges(navCounters)
    : baseSections;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden="true"
        className={cn(
          "fixed inset-0 z-40 transition-opacity duration-300 lg:hidden",
          adminPortal ? "bg-navy/55 backdrop-blur-sm" : "bg-[rgba(4,13,30,0.55)] backdrop-blur-[6px]",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      <aside
        aria-label={t("nav.sidebar")}
        data-collapsed={collapsed ? "true" : undefined}
        className={cn(
          
        "fixed inset-y-0 start-0 z-50 flex w-sidebar flex-col overflow-y-auto lg:translate-x-0 overflow-hidden overflow-y-auto",
          // Collapse to an icons-only rail only on desktop; the mobile off-canvas
          // drawer always opens at full width regardless of the saved pref.
          collapsed && "lg:w-16",
          "transition-[transform,width] duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none",
          adminPortal
            ? "bg-gradient-to-b from-navy via-navy-2 to-navy-3 shadow-[inset_-1px_0_0_rgba(26,111,208,0.12)] rtl:shadow-[inset_1px_0_0_rgba(26,111,208,0.12)] mesh-bg"
            : "border-s border-white/[0.055] bg-[linear-gradient(165deg,rgba(6,13,28,0.96)_0%,rgba(8,16,34,0.97)_100%)] backdrop-blur-[48px] backdrop-saturate-[160%] mesh-bg",
          "max-lg:shadow-[8px_0_32px_rgba(7,20,42,0.22)] rtl:max-lg:shadow-[-8px_0_32px_rgba(7,20,42,0.22)]",
          open
            ? "translate-x-0"
            : "max-lg:ltr:-translate-x-[110%] max-lg:rtl:translate-x-[110%]",
        )}
      >
        {!adminPortal && (
          <div
            aria-hidden
            className="pointer-events-none absolute -end-[60px] -top-20 h-[280px] w-[280px] rounded-full bg-[radial-gradient(circle,rgb(var(--color-primary)/0.12),transparent_65%)]"
          />
        )}
        <div
          className={cn(
            "flex items-center gap-3 border-b py-5",
            adminPortal ? "border-primary/15" : "border-white/[0.06]",
            // On the desktop rail, stack the icon-only logo above the toggle and
            // centre them so both fit inside the narrow (w-16) column.
            collapsed ? "px-5 lg:flex-col lg:items-center lg:gap-2 lg:px-2" : "px-5",
          )}
        >
          {/* Wordmark hides only on the desktop rail (lg); the full-width mobile
              drawer keeps the lockup even when the pref is collapsed. The div is
              still rendered and hidden via CSS so it can re-appear responsively. */}
          <SijilatyLogo size="sm" onDark className={cn(collapsed && "lg:[&>div]:hidden")} />
          <button
            type="button"
            onClick={() => setPersistedCollapsed(!collapsed)}
            aria-label={collapsed ? t("nav.expandSidebar") : t("nav.collapseSidebar")}
            aria-expanded={!collapsed}
            title={collapsed ? t("nav.expandSidebar") : t("nav.collapseSidebar")}
            data-testid="sidebar-collapse-toggle"
            className={cn(
              "ms-auto hidden h-7 w-7 shrink-0 place-items-center rounded-md text-white/55 transition-colors hover:bg-white/[0.08] hover:text-white/90 lg:grid",
              collapsed && "lg:ms-0",
            )}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              // Chevron points toward the inline-start edge when expanded
              // (collapse direction); RTL mirroring flips it automatically.
              className={cn("transition-transform motion-reduce:transition-none rtl:-scale-x-100", collapsed && "rotate-180")}
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
        </div>

        {adminPortal ? (
          <div
            className={cn(
              "relative z-[1] mx-3 mt-3 flex items-center gap-2.5 rounded-md px-3 py-2.5",
              "bg-primary/10",
              collapsed && "lg:mx-2 lg:justify-center lg:px-0",
            )}
          >
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-gradient-to-br from-primary to-accent text-xs font-bold text-white">
              {nameInitial(user?.central_name ?? user?.name ?? "م")}
            </div>
            <div className={cn("min-w-0", collapsed && "lg:hidden")}>
              <div className="truncate text-[11.5px] font-semibold text-white/90">
                {user?.central_name ?? t("nav.admin")}
              </div>
              <div className="mt-0.5 flex items-center gap-1 text-[9.5px] font-medium text-primary-2">
                {t("auth.portalAdmin")}
              </div>
            </div>
          </div>
        ) : (
          // Central-portal brand header (spec §C / demo §10): the centre's logo
          // (or a monogram fallback), the centre name in bold, and the current
          // admin's name as subtext. On the collapsed desktop rail we show only
          // the logo/monogram — consistent with the rail's icons-only look.
          <div
            className={cn(
              "relative z-[1] mx-3 mt-3 flex items-center gap-2.5 rounded-md border border-white/[0.06] bg-white/[0.04] px-3 py-2.5",
              collapsed && "lg:mx-2 lg:justify-center lg:px-0",
            )}
          >
            {centralLogoUrl ? (
              <img
                src={centralLogoUrl}
                alt={t("nav.centralLogoAlt")}
                className="h-8 w-8 shrink-0 rounded-md border border-white/10 object-cover"
              />
            ) : centralName ? (
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-gradient-to-br from-primary to-mint text-xs font-bold text-white">
                {nameInitial(centralName)}
              </div>
            ) : (
              // No centre identity at all → fall back to the Sijilaty brand mark.
              <div className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-md bg-white/[0.06]">
                <SijilatyLogo size="sm" onDark showWordmark={false} className="[&>svg]:h-6 [&>svg]:w-auto" />
              </div>
            )}
            <div className={cn("min-w-0", collapsed && "lg:hidden")}>
              <div className="truncate text-[11.5px] font-semibold text-white/90">
                {centralName || t("nav.centralBrand")}
              </div>
              <div className="mt-0.5 flex items-center gap-1 text-[9.5px] font-medium text-mint">
                <span className="h-1.5 w-1.5 rounded-full bg-green shadow-[0_0_6px_rgba(22,163,74,0.6)]" />
                <span className="truncate">{adminName || t("nav.user")}</span>
              </div>
            </div>
          </div>
        )}

        <nav className={cn("flex-1 px-3 py-2.5", collapsed && "lg:px-2")}>
          {sections.map((section, idx) => (
            <div key={idx} className="mb-2">
              {section.labelKey &&
                (collapsed ? (
                  // On the desktop rail the section title is replaced by a thin
                  // divider so groups stay visually separated without labels.
                  <>
                    <div className="px-2 pb-1 pt-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-white/25 lg:hidden">
                      {t(section.labelKey)}
                    </div>
                    {idx > 0 && <div className="mx-2 mb-1 mt-2 hidden h-px bg-white/[0.06] lg:block" />}
                  </>
                ) : (
                  <div className="px-2 pb-1 pt-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-white/25">
                    {t(section.labelKey)}
                  </div>
                ))}
                
              {section.items.map((item) =>
                item.children ? (
                  <CollapsibleNavItem
                    key={item.to}
                    item={item}
                    collapsed={collapsed}
                    adminPortal={adminPortal}
                    onClose={onClose}
                  />
                ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/" || item.to === "/admin"}
                  onClick={onClose}
                  // Native tooltip surfaces the label when the rail hides it.
                  title={collapsed ? t(item.labelKey) : undefined}
                  className={({ isActive }) =>
                    cn(
                      "relative mb-0.5 flex items-center gap-3 rounded-lg px-3 py-3 text-[13px] font-medium transition-all motion-reduce:transition-none",
                      collapsed && "lg:justify-center lg:px-0",
                      isActive
                        ? adminPortal
                          ? "border border-primary/25 bg-gradient-to-r from-primary/[0.18] to-accent/[0.08] text-white rtl:bg-gradient-to-l"
                          : "border border-primary/26 bg-primary/20 text-white shadow-[0_0_20px_rgb(var(--color-primary)/0.25),inset_0_0_28px_rgb(var(--color-primary)/0.12)]"
                        : "text-white/65 hover:bg-white/[0.08] hover:text-white/90",
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span
                          className={cn(
                            "absolute start-0 bottom-2 top-2 w-[3px] rounded-e",
                            collapsed && "lg:hidden",
                            adminPortal
                              ? "bg-primary-2"
                              : "brand-grad shadow-[0_0_8px_rgb(var(--color-primary)/0.6)]",
                          )}
                        />
                      )}
                      <span
                        className={cn(
                          "relative opacity-65",
                          isActive && (adminPortal ? "text-primary-2 opacity-100" : "text-primary-2 opacity-100"),
                        )}
                      >
                        {item.icon}
                        {item.badge && item.to !== "/sessions" && item.to !== "/admin/sessions" && (
                          <span
                            aria-hidden
                            className={cn(
                              "absolute -end-1.5 -top-1.5 hidden h-2 w-2 rounded-full bg-rose ring-2 ring-navy",
                              collapsed && "lg:block",
                            )}
                          />
                        )}
                      </span>
                      <span className={cn("flex-1", collapsed && "lg:hidden")}>{t(item.labelKey)}</span>
                      {item.badge && item.to !== "/sessions" && item.to !== "/admin/sessions" && (
                        <span
                          className={cn(
                            "num inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose px-1.5 text-[9.5px] font-bold text-white",
                            collapsed && "lg:hidden",
                          )}
                        >
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
                )
              )}
            </div>
          ))}
        </nav>

        <div
          className={cn(
            "flex items-center gap-2.5 border-t px-4 py-3.5 shadow-[0_-1px_12px_rgba(26,111,208,0.1)]",
            adminPortal ? "border-primary/25 bg-primary/5" : "border-white/[0.06] bg-white/[0.02]",
            collapsed && "lg:justify-center lg:px-2",
          )}
        >
          <div
            title={collapsed ? (user?.name ?? t("nav.user")) : undefined}
            className={cn(
              "grid h-9 w-9 shrink-0 place-items-center rounded-full border-2 bg-navy-3 text-xs font-bold text-white",
              adminPortal ? "border-primary/35" : "border-primary/35",
            )}
          >
            {nameInitial(user?.name)}
          </div>
          <div className={cn("min-w-0 flex-1", collapsed && "lg:hidden")}>
            <div className="truncate text-[12.5px] font-semibold text-white">{user?.name ?? t("nav.user")}</div>
            <div className="truncate text-[10px] text-white/55">{user?.email ?? ""}</div>
          </div>
        </div>
      </aside>
    </>
  );
}
